import api from "config/axios.config";
import { apiCache } from "utils/cache";

import { EFeatures } from "types/features.type";

import {
  DataFeatures,
  ECurrency,
  IClient,
  ICreateClient,
  SELECT_CUSTOMERS,
  Platform,
  ITriggerBuildResponse,
  IVerifyBuildResponse,
} from "types/client.type";
import { Design, ETypeDesign } from "pages/design/design.type";

/**
 * Get auth token from localStorage (Cognito)
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem("accessToken");
};

// Cache frequently accessed data for 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Gets all clients with limited fields
 * @returns Promise resolving to an array of client data
 * @throws Will throw an error if the API request fails
 */
export const getClients = apiCache.withCache(async (): Promise<IClient[]> => {
  try {
    return await api.get(`/clients?select=${SELECT_CUSTOMERS}&limit=1000`);
  } catch (error) {
    console.error("Error getting clients:", error);
    throw error;
  }
}, CACHE_TTL);

/**
 * Gets all clients for the current user with limited fields
 * @returns Promise resolving to an array of client data
 * @throws Will throw an error if the API request fails
 */
export const getMyClients = apiCache.withCache(async (): Promise<IClient[]> => {
  try {
    return await api.get(`/clients?select=${SELECT_CUSTOMERS}&limit=1000`);
  } catch (error) {
    console.error("Error getting my clients:", error);
    throw error;
  }
}, CACHE_TTL);

/**
 * Gets all available templates
 * @returns Promise resolving to an array of template data
 * @throws Will throw an error if the API request fails
 */
export const getTemplates = apiCache.withCache(async (): Promise<IClient[]> => {
  try {
    return await api.get(`/clients/templates`);
  } catch (error) {
    console.error("Error getting templates:", error);
    throw error;
  }
}, CACHE_TTL);

/**
 * Gets a client by ID
 * @param id - The client ID
 * @param select - Optional fields to select
 * @returns Promise resolving to client data
 * @throws Will throw an error if the API request fails
 */
export async function getClientsById(id: string, select?: string): Promise<IClient> {
  try {
    return await api.get(`clients/${select ? `${id}?select=${select}` : id}`);
  } catch (error) {
    console.error(`Error getting client with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Creates a new client
 * @param data - The client data
 * @returns Promise resolving to the created client data
 * @throws Will throw an error if the API request fails
 */
export async function createClient(data: ICreateClient): Promise<IClient> {
  try {
    return await api.post("clients", data);
  } catch (error) {
    console.error("Error creating client:", error);
    throw error;
  }
}

/**
 * Updates a client
 * @param id - The client ID
 * @param data - The data to update
 * @param select - Optional fields to select in the response
 * @returns Promise resolving to the updated client data
 * @throws Will throw an error if the API request fails
 */
export async function updateClient(
  id: string,
  data: FormData | object,
  select?: string
): Promise<IClient> {
  try {
    return await api.patch(`clients/${select ? `${id}?select=${select}` : id}`, data);
  } catch (error) {
    console.error(`Error updating client with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Updates a feature for a client
 * @param id - The client ID
 * @param feature - The feature to update
 * @param dataFeature - The feature data
 * @param select - Optional fields to select in the response
 * @returns Promise resolving to the updated client data
 * @throws Will throw an error if the API request fails
 */
export async function updateFeature(
  id: string,
  feature: EFeatures,
  dataFeature: DataFeatures,
  select?: string
): Promise<IClient> {
  try {
    return await api.patch(`clients/${id}/feature/${feature}${select ? `?select=${select}` : ""}`, {
      dataFeature,
    });
  } catch (error) {
    console.error(`Error updating feature ${feature} for client ${id}:`, error);
    throw error;
  }
}

/**
 * Updates design settings for a client
 * @param id - The client ID
 * @param design - The design type
 * @param dataDesign - The design data
 * @param select - Optional fields to select in the response
 * @returns Promise resolving to the updated client data
 * @throws Will throw an error if the API request fails
 */
export async function updateDesign(
  id: string,
  design: ETypeDesign,
  dataDesign: Design,
  select?: string
): Promise<IClient> {
  try {
    return await api.patch(
      `clients/${id}/design/${design}${select ? `?select=${select}` : ""}`,
      dataDesign
    );
  } catch (error) {
    console.error(`Error updating design ${design} for client ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes a client
 * @param id - The client ID
 * @returns Promise resolving to the deletion result
 * @throws Will throw an error if the API request fails
 */
export async function deleteClient(id: string) {
  try {
    return await api.delete(`clients/${id}`);
  } catch (error) {
    console.error(`Error deleting client with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Updates the currency for a client
 * @param clientId - The client ID
 * @param data - The currency data
 * @param select - Fields to select in the response
 * @returns Promise resolving to the updated client data
 * @throws Will throw an error if the API request fails
 */
export async function updateClientCurrency(
  clientId: string,
  data: { currency: ECurrency },
  select: string
): Promise<IClient> {
  try {
    return await api.patch(
      `clients/${clientId}/currency${select ? `?select=${select}` : ""}`,
      data
    );
  } catch (error) {
    console.error(`Error updating currency for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Verifies if a build can be triggered for a client
 * @param clientId - The client ID
 * @param platform - The platform (iOS or Android)
 * @returns Promise resolving to the verification result
 * @throws Will throw an error if the API request fails
 */
export async function verifyBuild(
  clientId: string,
  platform: Platform
): Promise<IVerifyBuildResponse> {
  try {
    return await api.get(`clients/${clientId}/verify-build?platform=${platform}`);
  } catch (error) {
    console.error(`Error verifying build for client ${clientId} on ${platform}:`, error);
    throw error;
  }
}

/**
 * Triggers a build for a client
 * @param clientId - The client ID
 * @param platform - The platform (iOS or Android)
 * @returns Promise resolving to the trigger result
 * @throws Will throw an error if the API request fails
 */
export async function triggerBuild(
  clientId: string,
  platform: Platform
): Promise<ITriggerBuildResponse> {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("No authentication token available");
    }
    return await api.post(`clients/${clientId}/trigger-build`, { platform, token });
  } catch (error) {
    console.error(`Error triggering build for client ${clientId} on ${platform}:`, error);
    throw error;
  }
}

/**
 * Gets the build status for a client
 * @param clientId - The client ID
 * @param platform - Optional platform filter
 * @returns Promise resolving to the build status
 * @throws Will throw an error if the API request fails
 */
export async function getBuildStatus(clientId: string, platform?: Platform) {
  try {
    let path = `clients/${clientId}/build-status`;
    if (platform) path += `?platform=${platform}`;
    return await api.get(path);
  } catch (error) {
    console.error(`Error getting build status for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Gets images for a client
 * @param clientId - The client ID
 * @returns Promise resolving to an array of image URLs
 * @throws Will throw an error if the API request fails
 */
export async function getImages(clientId: string): Promise<string[]> {
  try {
    return await api.get(`clients/${clientId}/images`);
  } catch (error) {
    console.error(`Error getting images for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Uploads an image for a client
 * @param clientId - The client ID
 * @param formData - The image data
 * @returns Promise resolving to the uploaded image URL
 * @throws Will throw an error if the API request fails
 */
export async function uploadImage(clientId: string, formData: FormData | object): Promise<string> {
  try {
    // Ensure we're sending FormData
    if (!(formData instanceof FormData)) {
      throw new Error("FormData is required for file upload");
    }

    return await api.post(`clients/${clientId}/medias/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (error) {
    console.error(`Error uploading image for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Deletes an image for a client
 * @param clientId - The client ID
 * @param imageUrl - The image URL
 * @param mediaId - The media ID (optional, but recommended for better performance)
 * @returns Promise resolving to the deletion result
 * @throws Will throw an error if the API request fails
 */
export async function deleteImage(clientId: string, imageUrl: string, mediaId?: string): Promise<void> {
  try {
    await api.delete(`clients/${clientId}/images`, {
      data: { imageUrl, mediaId },
    });
  } catch (error) {
    console.error(`Error deleting image ${imageUrl} for client ${clientId}:`, error);
    throw error;
  }
}

// Deployment API functions

export interface IDeployBuildPayload {
  platform: 'ios';
  profile?: 'production' | 'preview' | 'development';
  appVersion?: string;
  iosBuildNumber?: string;
  refreshProfiles?: boolean;
}

export interface IDeploySubmitPayload {
  platform: 'ios';
  profile?: 'production' | 'preview' | 'development';
}

export interface IDeployAssociatePayload {
  appVersion: string;
  buildNumber?: string;
}

export interface IDeployUpdateMetadataPayload {
  platform: 'ios';
  profile?: 'production' | 'preview' | 'development';
  updatePrivacy?: boolean;
  updateScreenshots?: boolean;
}

/**
 * Builds the application
 * @param clientId - The client ID
 * @param payload - Build configuration
 * @returns Promise resolving to the build result
 * @throws Will throw an error if the API request fails
 */
export async function deployBuild(
  clientId: string,
  payload: IDeployBuildPayload
): Promise<any> {
  try {
    return await api.post(`clients/${clientId}/deploy/build`, payload);
  } catch (error) {
    console.error(`Error building app for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Submits the build to App Store Connect
 * @param clientId - The client ID
 * @param payload - Submit configuration
 * @returns Promise resolving to the submit result
 * @throws Will throw an error if the API request fails
 */
export async function deploySubmit(
  clientId: string,
  payload: IDeploySubmitPayload
): Promise<any> {
  try {
    return await api.post(`clients/${clientId}/deploy/submit`, payload);
  } catch (error) {
    console.error(`Error submitting build for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Associates the build with a version
 * @param clientId - The client ID
 * @param payload - Associate configuration
 * @returns Promise resolving to the associate result
 * @throws Will throw an error if the API request fails
 */
export async function deployAssociate(
  clientId: string,
  payload: IDeployAssociatePayload
): Promise<any> {
  try {
    return await api.post(`clients/${clientId}/deploy/associate-build`, payload);
  } catch (error) {
    console.error(`Error associating build for client ${clientId}:`, error);
    throw error;
  }
}

/**
 * Updates app metadata
 * @param clientId - The client ID
 * @param payload - Metadata update configuration
 * @returns Promise resolving to the update result
 * @throws Will throw an error if the API request fails
 */
export async function deployUpdateMetadata(
  clientId: string,
  payload: IDeployUpdateMetadataPayload
): Promise<any> {
  try {
    return await api.post(`clients/${clientId}/deploy/update-metadata`, payload);
  } catch (error) {
    console.error(`Error updating metadata for client ${clientId}:`, error);
    throw error;
  }
}
